/**
 * Placements, and turning one into an employee.
 *
 * A placement is the revenue record — billing invoices against it — and it is
 * also where a candidate stops being someone we placed and becomes someone we
 * employ. Nothing previously made that second step: Placement.employee_id
 * existed for several phases with nothing writing it, so converting meant
 * retyping the person into the employee screen with no link back.
 *
 * Onboarding is supervisor-only here because the server requires an approver.
 * Showing a recruiter a button that can only 403 is worse than not showing it.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  FileSpreadsheet,
  KeyRound,
  Loader2,
  Paperclip,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentsPanel from '../components/DocumentsPanel';
import { downloadExport, exportErrorMessage } from '../lib/exportDownload';
import { client, errorMessage } from '../api/client';
import { money } from '../lib/money';
import { isSupervisor, useAuth } from '../store/auth';
import type { OnboardedResult, Page, Placement, PlacementStatus } from '../types';
import { titleCaseName } from '../lib/titleCase';

/** Only the fields the onboarding form needs — the full shapes live on the
 *  workplaces screen, which is the one that manages them. */
interface NamedRow {
  id: string;
  name: string;
}

const STATUS_TONE: Record<PlacementStatus, string> = {
  yet_to_join: 'bg-amber-100 text-amber-700',
  working: 'bg-emerald-100 text-emerald-700',
  drop: 'bg-red-100 text-red-700',
  not_joined: 'bg-red-100 text-red-700',
  rejected: 'bg-red-100 text-red-700',
  hold: 'bg-gray-200 text-gray-600',
  // Amber, not red. They dropped — but we honoured it, and a drop that was
  // replaced does not carry the same commercial consequence as one that was
  // not. Colouring it like the others would say the opposite.
  replacement_done: 'bg-amber-100 text-amber-800',
};

function OnboardDialog({
  placement,
  submitting,
  onCancel,
  onConfirm,
}: {
  placement: Placement;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (payload: Record<string, unknown>) => void;
}) {
  const [employeeCode, setEmployeeCode] = useState('');
  const [designation, setDesignation] = useState(placement.position ?? '');
  const [department, setDepartment] = useState('');
  const [dateOfJoining, setDateOfJoining] = useState(placement.dateOfJoining ?? '');
  const [shiftPolicyId, setShiftPolicyId] = useState('');
  const [officeId, setOfficeId] = useState('');
  const [createLogin, setCreateLogin] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');

  const offices = useQuery<Page<NamedRow>>({
    queryKey: ['offices', 'for-onboarding'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/offices', { params: { limit: 200 } })).data,
  });
  const policies = useQuery<Page<NamedRow>>({
    queryKey: ['shift-policies', 'for-onboarding'],
    queryFn: async () =>
      (await client.get('/api/v1/admin/shift-policies', { params: { limit: 200 } })).data,
  });

  return (
    <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/30 p-4">
      <form
        className="card w-full max-w-lg space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!employeeCode.trim()) return;
          onConfirm({
            employeeCode: employeeCode.trim(),
            designation: designation.trim() || null,
            department: department.trim() || null,
            dateOfJoining: dateOfJoining || null,
            shiftPolicyId: shiftPolicyId || null,
            officeId: officeId || null,
            createLogin,
            // Only meaningful alongside createLogin — the schema rejects it
            // otherwise, rather than silently ignoring it.
            ...(createLogin && loginEmail.trim()
              ? { loginEmail: loginEmail.trim() }
              : {}),
          });
        }}
      >
        <h3 className="text-sm font-semibold text-gray-900">
          Onboard {titleCaseName(placement.candidateName) ?? 'this candidate'}
        </h3>
        <p className="text-xs text-gray-500">
          Their position and joining date are carried across from the
          placement — change them here only if the final paperwork differs.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-gray-600">
            Employee Code
            <input
              spellCheck={false}
              className="input mt-1"
              value={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value)}
              placeholder="E1042"
              required
            />
          </label>

          <label className="text-xs text-gray-600">
            Joining Date
            <input
              className="input mt-1"
              type="date"
              value={dateOfJoining}
              onChange={(e) => setDateOfJoining(e.target.value)}
            />
          </label>

          <label className="text-xs text-gray-600">
            Designation
            <input
              spellCheck={false}
              className="input mt-1"
              value={designation}
              onChange={(e) => setDesignation(e.target.value)}
            />
          </label>

          <label className="text-xs text-gray-600">
            Department
            <input
              spellCheck={false}
              className="input mt-1"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              placeholder="Engineering"
            />
          </label>

          <label className="text-xs text-gray-600">
            Office
            <select
              className="input mt-1"
              value={officeId}
              onChange={(e) => setOfficeId(e.target.value)}
            >
              <option value="">Not set</option>
              {(offices.data?.items ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs text-gray-600">
            Shift Policy
            <select
              className="input mt-1"
              value={shiftPolicyId}
              onChange={(e) => setShiftPolicyId(e.target.value)}
            >
              <option value="">Not set</option>
              {(policies.data?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="rounded border border-gray-200 bg-gray-50 p-3">
          <label className="flex items-start gap-2 text-xs text-gray-700">
            <input
              type="checkbox"
              className="mt-0.5 rounded border-gray-300"
              checked={createLogin}
              onChange={(e) => setCreateLogin(e.target.checked)}
            />
            <span>
              Create a login for this person
              <span className="mt-0.5 block text-[11px] text-gray-500">
                Without one they cannot mark attendance or file timesheets. A
                temporary password is shown once and must be changed on first
                sign-in.
              </span>
            </span>
          </label>

          {createLogin && (
            <label className="mt-2 block text-xs text-gray-600">
              Login Email
              <input
                spellCheck={false}
                className="input mt-1"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="Leave blank to use the candidate's own email"
              />
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={!employeeCode.trim() || submitting}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Onboard
          </button>
        </div>
      </form>
    </div>
  );
}

function CredentialsPanel({
  password,
  employeeCode,
  onDismiss,
}: {
  password: string;
  employeeCode: string;
  onDismiss: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="card w-full max-w-md space-y-3">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-amber-600" />
          <h3 className="text-sm font-semibold text-gray-900">
            Temporary password for {employeeCode}
          </h3>
        </div>
        <p className="text-xs text-gray-500">
          This is shown once and cannot be read again. Pass it on now — if it
          is lost, the only way forward is a password reset from the Users
          screen.
        </p>
        <code className="block select-all rounded border border-amber-200 bg-amber-50 px-3 py-2 text-center font-mono text-base tracking-wider text-amber-900">
          {password}
        </code>
        <p className="text-[11px] text-gray-400">
          They will be asked to change it when they first sign in.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={() => {
              void navigator.clipboard?.writeText(password);
              toast.success('Copied');
            }}
          >
            Copy
          </button>
          <button type="button" className="btn-primary" onClick={onDismiss}>
            I have saved it
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PlacementsPage() {
  const queryClient = useQueryClient();
  const user = useAuth((s) => s.user);
  const canOnboard = isSupervisor(user);
  const [onboarding, setOnboarding] = useState<Placement | null>(null);
  const [filesFor, setFilesFor] = useState<Placement | null>(null);

  const exportPlacements = useMutation({
    mutationFn: async () => downloadExport({ kind: 'placements', format: 'xlsx' }),
    onSuccess: (name) => toast.success(`Saved ${name}`),
    onError: (error) => {
      void exportErrorMessage(error).then((message) => toast.error(message));
    },
  });
  const [issued, setIssued] = useState<{ password: string; code: string } | null>(
    null,
  );
  const [pendingOnly, setPendingOnly] = useState(false);
  /**
   * Narrow to the placements this person is the recruiter on.
   *
   * Sent to the server rather than filtered here, unlike "Not yet onboarded"
   * below: that one reads a field already on the row, while this one decides
   * which rows exist. Filtering in the browser would narrow the 200 the page
   * happened to fetch and call it the answer.
   *
   * The route has taken mine_only since it was written; nothing on the screen
   * ever set it.
   */
  const [mineOnly, setMineOnly] = useState(false);

  const placements = useQuery<Page<Placement>>({
    queryKey: ['placements', mineOnly],
    queryFn: async () =>
      (
        await client.get('/api/v1/placements', {
          params: { limit: 200, ...(mineOnly ? { mineOnly: true } : {}) },
        })
      ).data,
  });

  const onboard = useMutation({
    mutationFn: async ({
      placementId,
      payload,
    }: {
      placementId: string;
      payload: Record<string, unknown>;
    }) =>
      (await client.post(`/api/v1/placements/${placementId}/onboard`, payload))
        .data as OnboardedResult,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['placements'] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setOnboarding(null);
      toast.success(`Onboarded as ${data.employee.employeeCode}`);
      // A one-time password cannot go in a toast: it disappears on a timer,
      // and the only recovery is a reset. It gets a panel that stays until
      // somebody says they have it.
      if (data.temporaryPassword) {
        setIssued({
          password: data.temporaryPassword,
          code: data.employee.employeeCode,
        });
      }
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const all = placements.data?.items ?? [];
  const rows = pendingOnly ? all.filter((p) => p.employeeId === null) : all;

  return (
    <div className="space-y-6">
      <section className="card">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Placements</h2>
          {/* Approver-only server-side, and canOnboard is the same role test. */}
          {canOnboard && (
            <button
              type="button"
              className="btn-ghost text-xs"
              disabled={exportPlacements.isPending}
              onClick={() => exportPlacements.mutate()}
              title="Colour-coded by status, like the old tracker sheet"
            >
              {exportPlacements.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-3.5 w-3.5" />
              )}
              Excel
            </button>
          )}
        </div>
        <p className="mt-1 max-w-2xl text-xs text-gray-500">
          Candidates who accepted an offer. Onboarding one creates the employee
          record payroll pays and attendance tracks, carrying across what the
          placement already knows rather than asking for it again.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={pendingOnly}
              onChange={(e) => setPendingOnly(e.target.checked)}
            />
            Not yet onboarded
          </label>

          <label className="flex items-center gap-2 text-xs text-gray-600">
            <input
              type="checkbox"
              className="rounded border-gray-300"
              checked={mineOnly}
              onChange={(e) => setMineOnly(e.target.checked)}
            />
            Only mine
          </label>
        </div>
      </section>

      {placements.isLoading ? (
        <div className="flex justify-center py-10 text-gray-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : placements.isError ? (
        <div className="card flex items-center justify-between border border-red-200 bg-red-50">
          <p className="text-sm text-red-800">Could not load placements.</p>
          <button
            type="button"
            className="btn-ghost"
            onClick={() => void placements.refetch()}
          >
            Retry
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card text-sm text-gray-500">
          {pendingOnly ? 'Everyone placed has been onboarded.' : 'No placements yet.'}
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((placement) => (
            <section key={placement.id} className="card">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-gray-900">
                      {titleCaseName(placement.candidateName) ?? 'Unknown candidate'}
                    </h3>
                    <span
                      className={`rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                        STATUS_TONE[placement.status]
                      }`}
                    >
                      {placement.status.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {placement.clientName ?? 'Unknown client'}
                    {placement.position ? ` · ${placement.position}` : ''}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {placement.dateOfJoining
                      ? `Joining ${placement.dateOfJoining}`
                      : 'No joining date'}
                    {placement.ctcAnnual ? ` · ${money(placement.ctcAnnual)} a year` : ''}
                  </p>
                </div>

                {placement.employeeId ? (
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1 text-xs text-emerald-700">
                      <BadgeCheck className="h-4 w-4" />
                      Onboarded
                    </span>
                    {canOnboard && (
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => setFilesFor(placement)}
                      >
                        <Paperclip className="h-4 w-4" />
                        Files
                      </button>
                    )}
                  </div>
                ) : canOnboard ? (
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    onClick={() => setOnboarding(placement)}
                  >
                    <UserPlus className="h-4 w-4" />
                    Onboard
                  </button>
                ) : null}
              </div>
            </section>
          ))}
        </div>
      )}

      {filesFor?.employeeId && (
        <DocumentsPanel
          title={titleCaseName(filesFor.candidateName) ?? 'Employee'}
          employeeId={filesFor.employeeId}
          onClose={() => setFilesFor(null)}
        />
      )}

      {issued && (
        <CredentialsPanel
          password={issued.password}
          employeeCode={issued.code}
          onDismiss={() => setIssued(null)}
        />
      )}

      {onboarding && (
        <OnboardDialog
          placement={onboarding}
          submitting={onboard.isPending}
          onCancel={() => setOnboarding(null)}
          onConfirm={(payload) =>
            onboard.mutate({ placementId: onboarding.id, payload })
          }
        />
      )}
    </div>
  );
}
